"use client";

import { motion } from "framer-motion";
import { type LucideIcon, Mail, Phone, PhoneOutgoing, Timer, Voicemail } from "lucide-react";
import { type ReactNode, useRef } from "react";
import { cn } from "@/lib/utils";
import { BAR_COUNT, generatePlaceholderHeights } from "@/lib/waveform-utils";
import { Avatar } from "@/ui/elements";
import { EASE } from "../../home/use-case-tabs/constants";
import { useStickToBottom } from "../../home/use-case-tabs/use-stick-to-bottom";

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

export type Offer = { brand: string; logo?: string; title: string; detail: string; cta: string };

export type ThreadItem =
  | {
      kind: "message";
      id: string;
      direction: "incoming" | "outgoing";
      body: ReactNode;
      offer?: Offer;
    }
  | { kind: "event"; id: string; icon: LucideIcon; title: string; sub?: string }
  | { kind: "system"; id: string; text: string }
  | { kind: "call"; id: string; title: string; sub: string }
  | { kind: "voicemail"; id: string; duration: string; transcript: string }
  | {
      kind: "email";
      id: string;
      direction: "incoming" | "outgoing";
      subject: string;
      body: string;
    };

export type StatusTone = "muted" | "brand" | "success" | "warn";

export type Contact = {
  name: string;
  src?: string;
  status?: string;
  statusTone?: StatusTone;
};

const STATUS_CLASS: Record<StatusTone, string> = {
  muted: "text-muted-foreground",
  brand: "text-primary dark:text-[hsl(var(--brand-accent))]",
  success: "text-success",
  warn: "text-amber-600 dark:text-amber-400",
};

/* ------------------------------------------------------------------ */
/* Row renderers                                                      */
/* ------------------------------------------------------------------ */

function Bubble({
  direction,
  children,
}: {
  direction: "incoming" | "outgoing";
  children: ReactNode;
}) {
  const out = direction === "outgoing";
  return (
    <div className={cn("w-full", out ? "text-right" : "text-left")}>
      <div
        className={cn(
          "inline-block max-w-[15rem] whitespace-pre-line rounded-2xl border px-3 py-2 text-left text-[13px] leading-snug sm:max-w-[17rem]",
          out
            ? "rounded-br-md border-transparent bg-outgoing-bubble text-white"
            : "rounded-bl-md border-border bg-incoming-bubble text-foreground",
        )}
      >
        {children}
      </div>
    </div>
  );
}

function OfferCard({ offer }: { offer: Offer }) {
  return (
    <div className="mt-1.5 flex justify-end">
      <div className="w-[15rem] rounded-xl border border-border bg-background p-3 text-left shadow-sm sm:w-[17rem]">
        <div className="flex items-center gap-1.5">
          {offer.logo ? (
            // biome-ignore lint/performance/noImgElement: tiny decorative brand mark in a mock
            <img
              src={offer.logo}
              alt={offer.brand}
              className="h-4 w-4 shrink-0 rounded-[4px] object-cover"
            />
          ) : null}
          <span className="text-[10px] font-bold uppercase tracking-wide text-foreground">
            {offer.brand}
          </span>
        </div>
        <p className="mt-1.5 text-sm font-semibold text-foreground">{offer.title}</p>
        <p className="text-xs text-muted-foreground">{offer.detail}</p>
        <span className="mt-2 inline-block rounded-md bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground">
          {offer.cta}
        </span>
      </div>
    </div>
  );
}

function EventChip({ icon: Icon, title, sub }: { icon: LucideIcon; title: string; sub?: string }) {
  return (
    <div className="flex justify-center">
      <span className="inline-flex max-w-full flex-wrap items-center justify-center gap-x-1.5 rounded-2xl border border-border bg-background px-2.5 py-1 text-center text-[11px] shadow-sm">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-medium text-foreground">
          <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
          {title}
        </span>
        {sub ? <span className="whitespace-nowrap text-muted-foreground">· {sub}</span> : null}
      </span>
    </div>
  );
}

function SystemLine({ text }: { text: string }) {
  return (
    <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
      <Timer className="h-3 w-3" />
      <span>{text}</span>
    </div>
  );
}

function CallCard({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="flex w-full justify-end">
      <div className="inline-flex max-w-[17rem] items-center gap-2.5 rounded-2xl rounded-br-md border border-border bg-background px-3 py-2 text-left shadow-sm">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
          <PhoneOutgoing className="h-4 w-4 text-foreground" />
        </span>
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-foreground">{title}</p>
          <p className="text-[11px] text-muted-foreground">{sub}</p>
        </div>
      </div>
    </div>
  );
}

const WAVE = generatePlaceholderHeights("usecase-voicemail", BAR_COUNT);

function VoicemailCard({ duration, transcript }: { duration: string; transcript: string }) {
  return (
    <div className="w-full text-left">
      <div className="inline-block max-w-[17rem] rounded-2xl rounded-bl-md border border-border bg-incoming-bubble px-3 py-2.5 text-left">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-background">
            <Voicemail className="h-3.5 w-3.5 text-foreground" />
          </span>
          <div className="flex h-6 flex-1 items-center gap-[1.5px]">
            {WAVE.map((h, i) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: static decorative bars
                key={i}
                className="min-w-[2px] flex-1 rounded-full bg-foreground/25"
                style={{ height: `${h * 100}%` }}
              />
            ))}
          </div>
          <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
            {duration}
          </span>
        </div>
        <p className="mt-2 text-[13px] leading-snug text-foreground">
          <span className="text-muted-foreground">Transcript · </span>“{transcript}”
        </p>
      </div>
    </div>
  );
}

function EmailCard({
  direction,
  subject,
  body,
}: {
  direction: "incoming" | "outgoing";
  subject: string;
  body: string;
}) {
  const out = direction === "outgoing";
  return (
    <div className={cn("w-full", out ? "flex justify-end" : "flex justify-start")}>
      <div className="w-[16rem] max-w-full overflow-hidden rounded-xl border border-border bg-background text-left shadow-sm sm:w-[17rem]">
        <div className="flex items-center gap-1.5 border-b border-border px-3 py-1.5">
          <Mail className="h-3 w-3 text-muted-foreground" />
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {out ? "Email sent" : "Email received"}
          </span>
        </div>
        <div className="px-3 py-2">
          <p className="text-[12px] font-semibold text-foreground">{subject}</p>
          <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">{body}</p>
        </div>
      </div>
    </div>
  );
}

function Row({ item }: { item: ThreadItem }) {
  switch (item.kind) {
    case "event":
      return <EventChip icon={item.icon} title={item.title} sub={item.sub} />;
    case "system":
      return <SystemLine text={item.text} />;
    case "call":
      return <CallCard title={item.title} sub={item.sub} />;
    case "voicemail":
      return <VoicemailCard duration={item.duration} transcript={item.transcript} />;
    case "email":
      return <EmailCard direction={item.direction} subject={item.subject} body={item.body} />;
    case "message":
      return (
        <div>
          <Bubble direction={item.direction}>{item.body}</Bubble>
          {item.offer ? <OfferCard offer={item.offer} /> : null}
        </div>
      );
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/* Conversation card                                                  */
/* ------------------------------------------------------------------ */

export function Conversation({
  contact,
  items,
  visible,
  reduce,
  context,
  flow = false,
  className,
}: {
  contact: Contact;
  items: ThreadItem[];
  /** Number of items revealed (driven by scroll progress). */
  visible: number;
  reduce: boolean;
  /** Optional context band beneath the header (member chips, etc.). */
  context?: ReactNode;
  /** Grow to content height with no inner scroll (mobile stacked layout). */
  flow?: boolean;
  className?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  useStickToBottom(scrollRef, flow ? 0 : visible, reduce);

  const shown = items.slice(0, visible);
  const tone = contact.statusTone ?? "muted";

  return (
    <div
      className={cn(
        "flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-sm",
        flow ? "h-auto" : "h-full",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Avatar name={contact.name} src={contact.src} size="xs" />
        <div className="min-w-0">
          <p className="truncate text-[13px] font-medium leading-tight text-foreground">
            {contact.name}
          </p>
          {contact.status ? (
            <p className={cn("truncate text-[11px] font-medium leading-tight", STATUS_CLASS[tone])}>
              {contact.status}
            </p>
          ) : null}
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5 text-[11px] font-medium text-foreground sm:px-2.5 sm:py-1">
            <Mail className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Send email</span>
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5 text-[11px] font-medium text-foreground sm:px-2.5 sm:py-1">
            <Phone className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Call</span>
          </span>
        </div>
      </div>

      {context ? (
        <div className="border-b border-border bg-muted/30 px-3 py-2">{context}</div>
      ) : null}

      {/* Thread */}
      <div
        ref={scrollRef}
        className={cn(
          "space-y-3 px-3 py-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          flow ? "" : "flex-1 overflow-y-auto",
        )}
      >
        {shown.map((item) => (
          <motion.div
            key={item.id}
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <Row item={item} />
          </motion.div>
        ))}
      </div>
    </div>
  );
}
